/**
 * Time-zone helpers for call requests. Preferred windows are stored in business
 * time (the schedule's zone, e.g. Africa/Cairo); the visitor may sit elsewhere.
 * These convert a business-local wall time to the visitor's wall time with Intl
 * alone — no tz library.
 */

/** Offset (ms) of `zone` from UTC at the given instant. */
function zoneOffsetMs(instant: number, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - instant;
}

/** UTC instant of a wall-clock `date` (YYYY-MM-DD) + `time` (HH:MM) in `zone`. */
export function zonedInstant(date: string, time: string, zone: string): number {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  // Two passes settle DST edges where the first offset guess is an hour off.
  const first = guess - zoneOffsetMs(guess, zone);
  return guess - zoneOffsetMs(first, zone);
}

/** True when `zone` is a zone this runtime recognises. */
export function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** "HH:MM" of an instant in `zone`. */
export function wallTime(instant: number, zone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(instant));
}

/** Localised "Sun, 4 Oct" for a business date. */
export function dayLabel(date: string, zone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(zonedInstant(date, '12:00', zone)));
}
